import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

import StatusTag from '../StatusTag';
import MassTextCampaign from '../../../../../Model/MassTextCampaign';
import colors from '../../../../../styles/colors';
import { DateFormat, timeStampToStr } from '../../../../../utils/date';
import { EM_DASH } from '../../utils/constants';
import texts from '../../texts.json';

import {
  StatCardsRow,
  StatCard,
  StatValue,
  StatLabel,
  Eyebrow,
  SnapshotTable,
  SnapshotLabel,
  SnapshotValue,
} from './styles';
import { formatFilter } from './utils';

const { details } = texts.history;

const OverviewPanel = ({ campaign, coachName, sentByName }) => {
  const { totals } = campaign;
  const recipients = totals?.recipients ?? EM_DASH;
  // Totals are written once, when the campaign closes, so they are not meaningful before then.
  const counted = totals && !MassTextCampaign.isInFlight(campaign.status);
  const sent = counted ? totals.delivered : EM_DASH;
  const failed = counted ? MassTextCampaign.getFailedTotal(totals) : EM_DASH;
  const isReengagement = MassTextCampaign.isReengagement(campaign);
  const filterText = formatFilter(campaign.filterSnapshot, {
    consentedLabel: details.filterValues.consented,
    allLabel: details.filterValues.all,
  });

  return (
    <>
      <StatCardsRow>
        <StatCard>
          <StatLabel>{details.stats.recipients}</StatLabel>
          <StatValue>{recipients}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{details.stats.sent}</StatLabel>
          <StatValue tone="sent">{sent}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{details.stats.failed}</StatLabel>
          <StatValue tone="fail">{failed}</StatValue>
        </StatCard>
      </StatCardsRow>

      <Eyebrow>{details.snapshot.title}</Eyebrow>
      <SnapshotTable>
        <SnapshotLabel>{details.snapshot.coach}</SnapshotLabel>
        <SnapshotValue>{coachName || EM_DASH}</SnapshotValue>

        <SnapshotLabel>{details.snapshot.created}</SnapshotLabel>
        <SnapshotValue>
          {timeStampToStr(campaign.createdAt, DateFormat.DATE_FORMAT_TIME_MINUTES) || EM_DASH}
        </SnapshotValue>

        {!isReengagement && (
          <>
            <SnapshotLabel>{details.snapshot.createdBy}</SnapshotLabel>
            <SnapshotValue>{sentByName || EM_DASH}</SnapshotValue>
          </>
        )}

        <SnapshotLabel>{details.snapshot.dpl}</SnapshotLabel>
        <SnapshotValue>
          {campaign.dpl
            ? (
              <Box
                component="a"
                href={campaign.dpl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: colors.base.secondary,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {campaign.dpl}
              </Box>
            )
            : EM_DASH}
        </SnapshotValue>

        <SnapshotLabel>{details.snapshot.filter}</SnapshotLabel>
        <SnapshotValue>{filterText}</SnapshotValue>

        <SnapshotLabel>{details.snapshot.status}</SnapshotLabel>
        <SnapshotValue>
          {campaign.status
            ? <Box sx={{ display: 'inline-flex' }}><StatusTag status={campaign.status} /></Box>
            : EM_DASH}
        </SnapshotValue>
      </SnapshotTable>
    </>
  );
};

OverviewPanel.propTypes = {
  campaign: PropTypes.shape({
    status: PropTypes.string,
    dpl: PropTypes.string,
    createdAt: PropTypes.object,
    totals: PropTypes.object,
    filterSnapshot: PropTypes.object,
  }).isRequired,
  coachName: PropTypes.string,
  sentByName: PropTypes.string,
};

OverviewPanel.defaultProps = {
  coachName: '',
  sentByName: '',
};

export default OverviewPanel;
